import React, { useState, useEffect } from 'react';
import { Tag, Spin, Empty, Progress, Badge, Tooltip } from 'antd';
import {
  Ticket, Bug, Zap, BarChart3, FileText, CheckCircle, Clock,
  PauseCircle, Users, AlertTriangle, TrendingDown, ArrowUpRight,
} from 'lucide-react';
import { Doughnut, Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, BarElement, ArcElement, Filler,
  Tooltip as CTooltip, Legend as CLegend,
} from 'chart.js';
import useApiMethods from '../../lib/useApiMethods';
import { TICKETING_DATA } from '../../constants/api';
import ExportButton from '../../components/common/ExportButton';
import { MetricCard, HeadlineCard } from '../../components/insights';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  ArcElement, Filler, CTooltip, CLegend
);

const SupportOverview = () => {
  const { fetch } = useApiMethods();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      const res = await fetch(TICKETING_DATA, {}, setLoading);
      if (res?.status !== 'fail') setData(res?.data || res);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="flex items-center justify-center h-screen"><Spin size="large" /></div>;
  if (!data) return <Empty description="Unable to load data" className="mt-20" />;

  const { summary = {}, sprint_progress = [], team_workload = [], backlog_trend = [], ticket_inflow = [], bug_trend = [], client_requirements = {} } = data;
  const sprints = Array.isArray(sprint_progress) ? sprint_progress : sprint_progress ? [sprint_progress] : [];
  const cr = client_requirements;
  const cr_clients = cr.clients || [];
  const { open_tickets = 0, total_tickets = 0, by_type = {}, by_priority = [], production_bugs = 0 } = summary;
  const avgCompletion = sprints.length > 0 ? Math.round(sprints.reduce((s, sp) => s + (sp.completion_pct || 0), 0) / sprints.length) : 0;

  // ── Helpers ──
  // Bridge legacy KpiCard API → new MetricCard
  const KpiCard = ({ icon, iconBg = '', title, value, subtitle, alert }) => {
    const IconComp = icon?.type;
    const intent = alert ? 'negative'
      : iconBg.includes('emerald') || iconBg.includes('green') ? 'positive'
      : iconBg.includes('red') || iconBg.includes('rose') ? 'negative'
      : iconBg.includes('amber') || iconBg.includes('orange') || iconBg.includes('yellow') ? 'warning'
      : iconBg.includes('blue') || iconBg.includes('sky') || iconBg.includes('cyan') ? 'info'
      : iconBg.includes('indigo') || iconBg.includes('violet') || iconBg.includes('purple') ? 'primary'
      : 'neutral';
    return (
      <MetricCard icon={IconComp} intent={intent} title={title} value={value} subtitle={subtitle} />
    );
  };

  // ── Chart configs ──
  const lineOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { x: { grid: { display: false } }, y: { grid: { color: 'rgba(148,163,184,0.15)' } } },
  };
  const barOpts = (stacked = false) => ({
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: stacked ? { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } : { display: false } },
    scales: {
      x: { stacked, grid: { display: false } },
      y: { stacked, beginAtZero: true, grid: { color: 'rgba(148,163,184,0.15)' } },
    },
  });

  const typeEntries = Object.entries(by_type);

  return (
    <div className="p-6 space-y-6 bg-background min-h-screen">

      {/* ━━ Header ━━ */}
      <div>
        <h2 className="text-2xl font-bold text-foreground">Development</h2>
        <p className="text-sm text-muted-foreground">Ticket metrics and sprint progress from FixDesk</p>
      </div>

      {/* Headline */}
      <HeadlineCard
        state={production_bugs > 5 ? 'critical' : (open_tickets > 500 || avgCompletion < 30) ? 'warning' : 'healthy'}
        title={
          production_bugs > 5
            ? `${production_bugs} production bugs open — customer impact likely`
            : avgCompletion < 30 && sprints.length > 0
              ? `Sprint completion low at ${avgCompletion}% across ${sprints.length} sprints`
              : `${open_tickets} open tickets · ${sprints.length} active sprints at ${avgCompletion}% avg`
        }
        description={`${total_tickets} total tickets · ${(cr.total_requirements || 0)} client requirements tracked`}
        metrics={[
          { label: 'Open', value: open_tickets },
          { label: 'Bugs', value: production_bugs },
          { label: 'Sprints', value: sprints.length, hint: `${avgCompletion}% avg` },
          { label: 'CR Pending', value: cr.pending || 0 },
        ]}
      />

      {/* ━━ KPI Cards — Tickets ━━ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={<Ticket className="w-5 h-5 text-white" />} iconBg="bg-indigo-500"
          title="Open Tickets" value={open_tickets}
          subtitle={`${total_tickets} total across all statuses`}
        />
        <KpiCard
          icon={<Bug className="w-5 h-5 text-white" />}
          iconBg={production_bugs > 5 ? 'bg-red-500' : 'bg-gray-500'}
          title="Production Bugs" value={production_bugs}
          subtitle={production_bugs > 0 ? 'Customer impact risk' : 'All clear'}
          alert={production_bugs > 5}
        />
        <KpiCard
          icon={<Zap className="w-5 h-5 text-white" />} iconBg="bg-amber-500"
          title="Active Sprints" value={sprints.length}
          subtitle={sprints.length > 0 ? `${avgCompletion}% avg completion` : 'No active sprints'}
        />
        <KpiCard
          icon={<BarChart3 className="w-5 h-5 text-white" />} iconBg="bg-blue-500"
          title="Total Active" value={total_tickets}
        />
      </div>

      {/* ━━ KPI Cards — Client Requirements ━━ */}
      {(cr.total_requirements || 0) > 0 && (
        <>
          <h3 className="text-lg font-semibold text-foreground">Client Requirements</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              icon={<FileText className="w-5 h-5 text-white" />} iconBg="bg-violet-500"
              title="Total Requirements" value={cr.total_requirements || 0}
            />
            <KpiCard
              icon={<CheckCircle className="w-5 h-5 text-white" />} iconBg="bg-emerald-500"
              title="Completed" value={cr.completed || 0}
              subtitle={`${cr.total_requirements ? Math.round((cr.completed || 0) / cr.total_requirements * 100) : 0}% completion rate`}
            />
            <KpiCard
              icon={<Clock className="w-5 h-5 text-white" />} iconBg="bg-orange-500"
              title="Pending" value={cr.pending || 0}
              alert={(cr.pending || 0) > 100}
            />
            <KpiCard
              icon={<PauseCircle className="w-5 h-5 text-white" />} iconBg="bg-gray-400"
              title="Deferred" value={cr.deferred || 0}
            />
          </div>
        </>
      )}

      {/* ━━ Active Sprints ━━ */}
      {sprints.length > 0 && (
        <>
          <h3 className="text-lg font-semibold text-foreground">Sprint Progress</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {sprints.map((sp) => {
              const pct = sp.completion_pct || 0;
              const color = pct >= 70 ? '#10b981' : pct >= 40 ? '#6366f1' : '#f59e0b';
              return (
                <div key={sp.sprint_name} className="rounded-2xl bg-card border border-border shadow-sm p-5 hover:shadow-md transition-all">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="font-semibold text-foreground">{sp.sprint_name}</div>
                      {sp.team_name && <span className="text-xs text-muted-foreground">{sp.team_name}</span>}
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold" style={{ color }}>{pct}%</div>
                    </div>
                  </div>
                  <Progress percent={pct} showInfo={false} strokeColor={color} strokeWidth={6} className="mb-3" />
                  <div className="flex justify-between text-xs text-muted-foreground mb-3">
                    <span>{sp.start_date} — {sp.end_date}</span>
                    <span>{sp.total} tickets</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="px-2 py-1 rounded-lg text-xs font-medium bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400">{sp.done} Done</span>
                    <span className="px-2 py-1 rounded-lg text-xs font-medium bg-blue-50 dark:bg-blue-950/30 text-blue-600">{sp.in_progress} Active</span>
                    <span className="px-2 py-1 rounded-lg text-xs font-medium bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400">{sp.pending} Pending</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ━━ Charts Row 1: Type + Backlog ━━ */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Type Doughnut + Priority */}
        <div className="lg:col-span-4 rounded-2xl bg-card border border-border shadow-sm p-5">
          <h4 className="font-semibold text-foreground mb-4">By Type</h4>
          {typeEntries.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <div style={{ height: 220 }}>
              <Doughnut
                data={{
                  labels: typeEntries.map(([k]) => k),
                  datasets: [{
                    data: typeEntries.map(([, v]) => v),
                    backgroundColor: typeEntries.map(([k]) => ({ Story: '#6366f1', Bug: '#ef4444', Epic: '#f59e0b' }[k] || '#94a3b8')),
                    borderWidth: 0,
                  }],
                }}
                options={{ responsive: true, maintainAspectRatio: false, cutout: '60%', plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } } }}
              />
            </div>
          )}
          {/* Priority bars */}
          {by_priority.length > 0 && (
            <div className="mt-5 space-y-2">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">By Priority</h4>
              {by_priority.map((p) => {
                const colorMap = { Critical: '#ef4444', High: '#f97316', Medium: '#eab308', Low: '#10b981' };
                const pct = open_tickets > 0 ? Math.round((p.count / open_tickets) * 100) : 0;
                return (
                  <div key={p.priority}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-muted-foreground">{p.priority}</span>
                      <span className="text-muted-foreground">{p.count} ({pct}%)</span>
                    </div>
                    <Progress percent={pct} showInfo={false} strokeColor={colorMap[p.priority] || '#94a3b8'} size="small" />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Backlog Trend */}
        <div className="lg:col-span-8 rounded-2xl bg-card border border-border shadow-sm p-5">
          <h4 className="font-semibold text-foreground mb-4">Backlog Trend</h4>
          {backlog_trend.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <div style={{ height: 320 }}>
              <Line
                data={{
                  labels: backlog_trend.map(b => b.week),
                  datasets: [{
                    label: 'Open', data: backlog_trend.map(b => b.count),
                    borderColor: '#6366f1', backgroundColor: 'rgba(99,102,241,0.08)',
                    borderWidth: 2, pointRadius: 4, pointBackgroundColor: '#6366f1', tension: 0.4, fill: true,
                  }],
                }}
                options={lineOpts}
              />
            </div>
          )}
        </div>
      </div>

      {/* ━━ Charts Row 2: Inflow + Bug Trend ━━ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <h4 className="font-semibold text-foreground mb-4">Ticket Inflow</h4>
          {ticket_inflow.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
            <div style={{ height: 260 }}>
              <Bar
                data={{
                  labels: ticket_inflow.map(b => b.week),
                  datasets: [{ data: ticket_inflow.map(b => b.count), backgroundColor: '#8b5cf6', borderRadius: 6, barThickness: 24 }],
                }}
                options={barOpts()}
              />
            </div>
          )}
        </div>
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <h4 className="font-semibold text-foreground mb-4">Bug Trend</h4>
          {bug_trend.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
            <div style={{ height: 260 }}>
              <Bar
                data={{
                  labels: bug_trend.map(b => b.week),
                  datasets: [
                    { label: 'Production', data: bug_trend.map(b => b.production), backgroundColor: '#ef4444', borderRadius: 6 },
                    { label: 'Development', data: bug_trend.map(b => b.development), backgroundColor: '#f59e0b', borderRadius: 6 },
                  ],
                }}
                options={barOpts(true)}
              />
            </div>
          )}
        </div>
      </div>

      {/* ━━ Team Workload ━━ */}
      {team_workload.length > 0 && (
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-indigo-500" />
            <h4 className="font-semibold text-foreground">Team Workload</h4>
            <span className="text-xs text-muted-foreground ml-auto mr-3">{team_workload.length} members</span>
            <ExportButton
              data={team_workload}
              filename="team-workload"
              pdfColumns={[{title:'Name',dataKey:'name'},{title:'Total',dataKey:'total'},{title:'In Progress',dataKey:'in_progress'},{title:'Pending',dataKey:'pending'},{title:'Dev Hours',dataKey:'dev_hours'}]}
              pdfTitle="Team Workload Report"
            />
          </div>
          <div className="space-y-3">
            {team_workload.slice(0, 12).map((w) => {
              const doneCount = (w.total || 0) - (w.pending || 0);
              const pct = w.total > 0 ? Math.round(doneCount / w.total * 100) : 0;
              return (
                <div key={w.name} className="flex items-center gap-4">
                  <div className="w-32 truncate text-sm font-medium text-foreground">{w.name}</div>
                  <div className="flex-1">
                    <Tooltip title={`${w.in_progress} active · ${w.pending} pending · ${w.dev_hours}h est`}>
                      <Progress
                        percent={pct}
                        strokeColor={pct >= 60 ? '#10b981' : pct >= 30 ? '#6366f1' : '#f59e0b'}
                        size="small"
                        format={() => <span className="text-xs text-muted-foreground">{doneCount}/{w.total}</span>}
                      />
                    </Tooltip>
                  </div>
                  <div className="flex gap-2 text-xs shrink-0">
                    <span className="px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-950/30 text-blue-600">{w.in_progress} active</span>
                    <span className="px-2 py-0.5 rounded bg-gray-100 text-muted-foreground">{w.dev_hours}h</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ━━ Client Requirements Table ━━ */}
      {cr_clients.length > 0 && (
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-violet-500" />
            <h4 className="font-semibold text-foreground">Client Requirements Breakdown</h4>
            <ExportButton
              data={cr_clients}
              filename="client-requirements"
              pdfColumns={[{title:'Client',dataKey:'client_name'},{title:'Project',dataKey:'project'},{title:'Total',dataKey:'total'},{title:'Completed',dataKey:'completed'},{title:'Pending',dataKey:'pending'},{title:'Deferred',dataKey:'deferred'}]}
              pdfTitle="Client Requirements Report"
            />
          </div>
          <div className="space-y-3">
            {cr_clients.map((client) => {
              const completionPct = client.total > 0 ? Math.round((client.completed || 0) / client.total * 100) : 0;
              return (
                <div key={client.client_code || client.client_name} className="flex items-center gap-4 p-3 rounded-xl bg-background hover:bg-gray-100/50 transition-colors">
                  <div className="w-40 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{client.client_name}</div>
                    {client.project && <div className="text-[10px] text-muted-foreground">{client.project}</div>}
                  </div>
                  <div className="flex-1">
                    <Progress
                      percent={completionPct}
                      strokeColor={completionPct >= 70 ? '#10b981' : completionPct >= 40 ? '#6366f1' : '#f59e0b'}
                      size="small"
                      format={() => <span className="text-xs text-muted-foreground">{completionPct}%</span>}
                    />
                  </div>
                  <div className="flex gap-2 text-xs shrink-0">
                    <span className="px-2 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400">{client.completed || 0}</span>
                    <span className="px-2 py-0.5 rounded bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400">{client.pending || 0}</span>
                    {(client.deferred || 0) > 0 && (
                      <span className="px-2 py-0.5 rounded bg-gray-100 text-muted-foreground">{client.deferred} deferred</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">{client.total} total</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportOverview;
